import { TranslocoModule, provideTranslocoScope, translate } from '@jsverse/transloco';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoCurrencyPipe } from '@jsverse/transloco-locale';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatBadgeModule } from '@angular/material/badge';
import { MatTabsModule } from '@angular/material/tabs';
import { MatChipsModule } from '@angular/material/chips';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatExpansionModule } from '@angular/material/expansion';
import { TableView, TypeOfColumn, TableColumnInfo } from 'design-system';
import { DynamicRouteService } from 'core';
import { ProductDetail, ProductDocument } from '../../interfaces/product-detail.model';
import { AvailabilityChip } from '../components/availability-chip/availability-chip';
import { AddToCartButton } from '../components/add-to-cart-button/add-to-cart-button';
import { ProductsService } from '../../services/products.service';
import { CartService } from '../../services/cart.service';
import { PRODUCTS_DESCRIBE } from '../constants';

interface BreadcrumbItem {
  label: string;
  link?: string;
}

@Component({
  providers: [provideTranslocoScope('products')],
  selector: 'prd-products-detail',
  imports: [
    TranslocoModule,
    MatCardModule,
    MatButtonModule,
    MatBadgeModule,
    MatTabsModule,
    MatChipsModule,
    MatButtonToggleModule,
    MatExpansionModule,
    TableView,
    TranslocoCurrencyPipe,
  ],
  templateUrl: './products-detail.html',
  styleUrl: './products-detail.scss',
})
export class ProductsDetail implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly dynamicRouteService = inject(DynamicRouteService);
  private readonly productsService = inject(ProductsService);
  private readonly destroyRef = inject(DestroyRef);
  protected readonly cart = inject(CartService);

  productDetail: ProductDetail | null = null;
  documents: ProductDocument[] = [];
  documentTypeFilter = '';

  get filteredDocuments(): ProductDocument[] {
    if (!this.documentTypeFilter) {
      return this.documents;
    }
    return this.documents.filter(doc => doc.tipo === this.documentTypeFilter);
  }

  breadcrumbItems: BreadcrumbItem[] = [
    { get label() { return translate<string>('products.ui.prodotti'); }, link: '/products' },
  ];

  ngOnInit(): void {
    // Aperto dalla selezione prodotti di una proposta: si lavora sul suo carrello
    const idCarrello = this.route.snapshot.queryParamMap.get('idCarrello');
    (idCarrello ? this.cart.getCart(idCarrello) : this.cart.getActiveCart())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: error => {
          console.error('Errore durante il caricamento del carrello', error);
        },
      });

    this.route.paramMap.subscribe(params => {
      const productId = params.get('productId');

      if (!productId) {
        console.error('ID prodotto mancante in URL');
        return;
      }

      this.loadProduct(productId);
      this.loadDocuments(productId);
    });
  }

  openRelatedProduct(productId: string): void {
    let navigationArray = this.dynamicRouteService.getRoutePath(this.router, PRODUCTS_DESCRIBE, 'product-detail');
    if (navigationArray) {
      navigationArray = this.dynamicRouteService.replacePathSegments(navigationArray, [productId]);
      this.router.navigate(navigationArray, {});
    }
  }

  private loadProduct(productId: string): void {
    this.productsService.getProductDetail(productId).subscribe({
      next: (data: ProductDetail) => {
        this.productDetail = data;
        this.breadcrumbItems = [
          { get label() { return translate<string>('products.ui.prodotti'); }, link: '/products' },
          { label: data.plc1 },
          { label: data.plc2 },
          { label: data.nomeProdotto },
        ];
      },
      error: err => {
        console.error('Errore caricamento dettaglio prodotto', err);
      },
    });
  }

  private loadDocuments(productId: string): void {
    this.productsService.getProductDocuments(productId).subscribe({
      next: (data: ProductDocument[]) => {
        this.documents = data;
      },
      error: err => {
        console.error('Errore caricamento documenti prodotto', err);
      },
    });
  }

  downloadDocument(doc: ProductDocument): void {
    this.productsService.downloadProductDocument(doc.idAsset).subscribe({
      next: (blob: Blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = doc.nomeFile;
        a.click();
        URL.revokeObjectURL(url);
      },
      error: err => {
        console.error('Errore download documento', err);
      },
    });
  }

  get availableCodesTableData() {
    return (this.productDetail?.codiciDisponibili ?? []).map(p => ({
      ...p,
      'imballoDescrizione': `${p.nomeImballo} - ${p.unitaFatturabile} - ${p.unitaMisura}`,
    }));
  }

  get chemicalPropertiesTableData() {
    return (this.productDetail?.pimcore.proprietaChimiche ?? []).map(p => ({
      ...p,
      'metodo': p.metodo ?? '-',
      'unita': p.unita ?? '-',
    }));
  }

  chemicalPropertiesColumnsInfo: TableColumnInfo[] = [
    {
      columnNameDef: 'caratteristica',
      get label() { return translate<string>('products.ui.caratteristica'); },
      type: TypeOfColumn.TEXT,
      sortable: false,
      labelOnOneLine: true,
    },
    {
      columnNameDef: 'metodo',
      get label() { return translate<string>('products.ui.metodo'); },
      type: TypeOfColumn.TEXT,
      sortable: false,
      labelOnOneLine: true,
    },
    {
      columnNameDef: 'unita',
      get label() { return translate<string>('products.ui.unita'); },
      type: TypeOfColumn.TEXT,
      sortable: false,
      labelOnOneLine: true,
    },
    {
      columnNameDef: 'valore',
      get label() { return translate<string>('products.ui.valore'); },
      type: TypeOfColumn.TEXT,
      sortable: false,
      labelOnOneLine: true,
    },
  ];

  specificationsColumnsInfo: TableColumnInfo[] = [
    {
      columnNameDef: 'sorgente',
      get label() { return translate<string>('products.ui.sorgente'); },
      type: TypeOfColumn.TEXT,
      sortable: false,
      labelOnOneLine: true,
    },
    {
      columnNameDef: 'sigla',
      get label() { return translate<string>('products.ui.sigla'); },
      type: TypeOfColumn.TEXT,
      sortable: false,
      labelOnOneLine: true,
    },
  ];

  availableCodesColumnsInfo: TableColumnInfo[] = [
    {
      columnNameDef: 'sku',
      get label() { return translate<string>('products.ui.codProdotto'); },
      type: TypeOfColumn.TEXT,
      sortable: false,
      labelOnOneLine: true,
    },
    {
      columnNameDef: 'nomeProdotto',
      get label() { return translate<string>('products.ui.nomeProdotto'); },
      type: TypeOfColumn.TEXT,
      sortable: false,
      labelOnOneLine: true,
    },
    {
      columnNameDef: 'imballoDescrizione',
      get label() { return translate<string>('products.ui.imballoQTaUom'); },
      type: TypeOfColumn.TEXT,
      sortable: false,
      labelOnOneLine: true,
    },
    {
      columnNameDef: 'scala',
      get label() { return translate<string>('products.ui.scala'); },
      type: TypeOfColumn.TEXT,
      sortable: false,
      labelOnOneLine: true,
    },
    {
      columnNameDef: 'prezzo',
      get label() { return translate<string>('products.ui.prezzo'); },
      type: TypeOfColumn.CURRENCY,
      sortable: false,
      minimumFractionDigits: 4,
      maximumFractionDigits: 4,
      labelOnOneLine: true,
    },
    {
      columnNameDef: 'plc1',
      label: 'PLC1',
      type: TypeOfColumn.TEXT,
      sortable: false,
      labelOnOneLine: true,
    },
    {
      columnNameDef: 'plc2',
      label: 'PLC2',
      type: TypeOfColumn.TEXT,
      sortable: false,
      labelOnOneLine: true,
    },
    {
      columnNameDef: 'stock',
      get label() { return translate<string>('products.ui.stock'); },
      type: TypeOfColumn.COMPONENT,
      sortable: false,
      component: AvailabilityChip,
      labelOnOneLine: true,
      cellClass: () => 'cell-align-right',
    },
    {
      columnNameDef: 'idProdotto',
      label: '',
      type: TypeOfColumn.COMPONENT,
      sortable: false,
      component: AddToCartButton,
    },
  ];
}
